import React, { createContext, useContext, useEffect, useState, useCallback, useRef, useMemo, Component, ErrorInfo, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface UserPresenceContextType {
  onlineUsers: Set<string>;
  isOnline: (userId: string) => boolean;
}

type PresenceRecord = {
  is_online: boolean;
  last_seen: string | null;
  updated_at?: string | null;
};

const ONLINE_STALE_THRESHOLD_MS = 5 * 60 * 1000; // 5 minutes (more strict)

const UserPresenceContext = createContext<UserPresenceContextType | undefined>(undefined);

// Default safe values for when auth is not ready or errors occur
const defaultContextValue: UserPresenceContextType = {
  onlineUsers: new Set<string>(),
  isOnline: () => false,
};

// Internal error boundary to catch errors in the presence logic
class PresenceErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.warn('🚫 UserPresence: Error caught by internal boundary, using degraded mode:', error.message);
  }

  render() {
    if (this.state.hasError) {
      // Return children wrapped with default context so app continues to work
      return (
        <UserPresenceContext.Provider value={defaultContextValue}>
          {this.props.children}
        </UserPresenceContext.Provider>
      );
    }
    return this.props.children;
  }
}

// Inner component that does the actual presence work
const UserPresenceProviderInner: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Store richer presence data, not just a boolean
  const [presenceMap, setPresenceMap] = useState<Map<string, PresenceRecord>>(new Map());
  // Heartbeat tick to re-evaluate "stale" without DB writes
  const [tick, setTick] = useState(0);

  // Get user from auth context - may be null during loading
  const { user } = useAuth();
  
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const retryCountRef = useRef<number>(0);
  const lastUpdateRef = useRef<number>(0);
  const isUpdatingRef = useRef<boolean>(false);
  const presenceChannelRef = useRef<any | null>(null);
  const presenceSubscribedRef = useRef<boolean>(false);

  
  // Insert/update a single presence record locally
  const updatePresenceRecord = useCallback((userId: string, is_online: boolean, last_seen?: string | null, updated_at?: string | null) => {
    setPresenceMap(prev => {
      const next = new Map(prev);
      const existing = next.get(userId);
      
      const now = new Date().toISOString();
      next.set(userId, {
        is_online,
        last_seen: last_seen ?? existing?.last_seen ?? now,
        updated_at: updated_at ?? existing?.updated_at ?? now,
      });
      return next;
    });
  }, []);

  // Compute if user is online using last_seen freshness with stricter logic
  const isOnline = useCallback((userId: string) => {
    const rec = presenceMap.get(userId);
    if (!rec || !rec.is_online) return false;
    
    const lastSeenMs = rec.last_seen ? new Date(rec.last_seen).getTime() : 0;
    const isRecent = Date.now() - lastSeenMs <= ONLINE_STALE_THRESHOLD_MS;
    
    // Only consider online if both conditions are met
    return rec.is_online && isRecent;
  }, [presenceMap, tick]);

  // Derived online users Set based on freshness
  const derivedOnlineUsers = useMemo(() => {
    const set = new Set<string>();
    presenceMap.forEach((rec, id) => {
      if (!rec) return;
      const lastSeenMs = rec.last_seen ? new Date(rec.last_seen).getTime() : 0;
      const isRecent = Date.now() - lastSeenMs <= ONLINE_STALE_THRESHOLD_MS;
      if (rec.is_online && isRecent) set.add(id);
    });
    return set;
  }, [presenceMap, tick]);

  // Exponential backoff for retry logic
  const getRetryDelay = (retryCount: number) => {
    return Math.min(1000 * Math.pow(2, retryCount), 30000); // Max 30 seconds
  };

  // Minimum interval for forced updates (e.g. visibilitychange) to prevent storm
  const FORCE_UPDATE_MIN_INTERVAL_MS = 10000; // 10 seconds

  // Rate-limited presence update with authentication check
  const updatePresence = useCallback(async (forceUpdate = false) => {
    if (!user || isUpdatingRef.current) {
      return;
    }

    const now = Date.now();
    const timeSinceLast = now - lastUpdateRef.current;

    // Regular updates: 30s throttle. Forced updates (visibility/mount): 10s minimum throttle.
    const minInterval = forceUpdate ? FORCE_UPDATE_MIN_INTERVAL_MS : 30000;
    if (timeSinceLast < minInterval) {
      return;
    }

    const { data } = await supabase.auth.getSession();
    const sessionUserId = data.session?.user?.id;
    if (!sessionUserId) {
      console.log('👤 UserPresence: No active session, skipping presence update');
      return;
    }

    if (sessionUserId !== user.id) {
      console.warn('👤 UserPresence: Session/user mismatch, skipping presence update', { sessionUserId, userId: user.id });
      return;
    }

    isUpdatingRef.current = true;

    // Stamp the time immediately to prevent concurrent forced calls from slipping through
    lastUpdateRef.current = now;

    try {
      const currentTime = new Date().toISOString();
      const { error: upsertError } = await supabase
        .from('user_presence')
        .upsert({
          user_id: sessionUserId,
          is_online: true,
          last_seen: currentTime,
          updated_at: currentTime
        });

      if (upsertError) {
        console.warn('🚫 UserPresence: Error updating presence:', upsertError.message);
        // Reset timestamp so next attempt is allowed
        lastUpdateRef.current = 0;
        const msg = upsertError.message.toLowerCase();
        if (msg.includes('rls') || msg.includes('row-level security') || msg.includes('forbidden') || msg.includes('403')) {
          retryCountRef.current++;
          const retryDelay = getRetryDelay(retryCountRef.current);
          setTimeout(() => updatePresence(false), retryDelay);
        }
      } else {
        retryCountRef.current = 0;
        updatePresenceRecord(sessionUserId, true, currentTime);
      }
    } catch (error) {
      console.warn('🚫 UserPresence: Exception during presence update:', error);
      lastUpdateRef.current = 0;
      retryCountRef.current++;
    } finally {
      isUpdatingRef.current = false;
    }
  }, [user, updatePresenceRecord]);

  // Set user offline (database)
  const setOffline = useCallback(async () => {
    if (!user) return;

    const { data } = await supabase.auth.getSession();
    const sessionUserId = data.session?.user?.id;
    const currentTime = new Date().toISOString();

    try {
      if (sessionUserId && sessionUserId === user.id) {
        console.log('🔴 UserPresence: Setting user offline:', sessionUserId);
        await supabase
          .from('user_presence')
          .upsert({
            user_id: sessionUserId,
            is_online: false,
            last_seen: currentTime,
            updated_at: currentTime
          });
      } else {
        console.log('🔴 UserPresence: Skipping remote offline update due to missing/mismatched session');
      }
    } catch (error) {
      console.warn('🚫 UserPresence: Error setting offline:', error);
    } finally {
      // Update local cache regardless
      updatePresenceRecord(user.id, false, currentTime);
    }
  }, [user, updatePresenceRecord]);

  // Lightweight heartbeat to naturally clear stale online records in UI
  useEffect(() => {
    const hb = setInterval(() => setTick(t => t + 1), 30000); // 30s instead of 60s
    return () => clearInterval(hb);
  }, []);

  useEffect(() => {
    if (!user) {
      console.log('👤 UserPresence: No user, clearing presence map');
      setPresenceMap(new Map());
      return;
    }

    console.log('🟢 UserPresence: Setting up presence for user:', user.id);

    // Clear any existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    const setupPresence = async () => {
      try {
        // Test authentication and table access with graceful degradation
        const { error: testError } = await supabase
          .from('user_presence')
          .select('user_id')
          .limit(1);

        if (testError) {
          console.warn('🚫 UserPresence: Cannot access user_presence table, continuing with degraded mode:', testError.message);
          // Continue without throwing - graceful degradation
          // Still set up the user as "online" locally for UI consistency
          const currentTime = new Date().toISOString();
          updatePresenceRecord(user.id, true, currentTime);
          return;
        }

        // Set user online with a short delay to allow auth to stabilize
        await new Promise((r) => setTimeout(r, 400));
        await updatePresence(true);

        // Set up interval for periodic updates (30 seconds instead of 60)
        intervalRef.current = setInterval(() => {
          updatePresence();
        }, 30000);

        // Clean up any existing presence channels for this user (by topic)
        const existingChannels = supabase.getChannels();
        const userPresenceChannels = existingChannels.filter(ch =>
          ch.topic?.includes?.('user-presence') && ch.topic?.includes?.(user.id)
        );
        userPresenceChannels.forEach(channel => {
          console.log('👤 UserPresence: Removing existing presence channel:', channel.topic);
          try { supabase.removeChannel(channel); } catch (e) { console.warn('⚠️ UserPresence: Error removing old channel', e); }
        });

        // Also remove any channel referenced in our ref
        if (presenceChannelRef.current) {
          try { supabase.removeChannel(presenceChannelRef.current); } catch (e) { console.warn('⚠️ UserPresence: Error removing ref channel', e); }
          presenceChannelRef.current = null;
          presenceSubscribedRef.current = false;
        }

        // Use unique channel name to avoid conflicts
        const channelName = `user-presence-${user.id}-${Date.now()}-${Math.random().toString(36).slice(2,8)}`;

        // Create channel and listen for presence changes
        const channel = supabase
          .channel(channelName)
          .on(
            'postgres_changes',
            {
              event: '*',
              schema: 'public',
              table: 'user_presence'
            },
            (payload) => {
              console.log('👤 UserPresence: Presence change:', payload);
              if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE') {
                const presence = payload.new as any;
                updatePresenceRecord(
                  presence.user_id,
                  !!presence.is_online,
                  presence.last_seen ?? null,
                  presence.updated_at ?? null
                );
              }
            }
          );

        presenceChannelRef.current = channel;

        // Subscribe only once per channel instance with enhanced error handling
        if (!presenceSubscribedRef.current) {
          try {
            channel.subscribe((status) => {
              console.log('👤 UserPresence: subscription status:', status);
              if (status === 'CHANNEL_ERROR') {
                console.warn('⚠️ UserPresence: Channel subscription error, retrying...');
                presenceSubscribedRef.current = false;
                // Retry subscription after a delay
                setTimeout(() => {
                  if (presenceChannelRef.current && !presenceSubscribedRef.current) {
                    try {
                      presenceChannelRef.current.subscribe();
                      presenceSubscribedRef.current = true;
                    } catch (retryErr) {
                      console.warn('🚫 UserPresence: Retry subscription failed:', retryErr);
                    }
                  }
                }, 2000);
              }
            });
            presenceSubscribedRef.current = true;
          } catch (err) {
            console.warn('🚫 UserPresence: subscribe error (already subscribed?)', err);
            // Don't set subscribed flag if there was an error
          }
        }

        // Fetch initial presence records (we need last_seen)
        const { data, error: fetchError } = await supabase
          .from('user_presence')
          .select('user_id, is_online, last_seen, updated_at')
          .order('updated_at', { ascending: false });

        if (fetchError) {
          console.warn('🚫 UserPresence: Cannot fetch initial presence:', fetchError.message);
        } else if (data) {
          console.log('👤 UserPresence: Initial presence rows:', data.length);
          setPresenceMap(() => {
            const next = new Map<string, PresenceRecord>();
            data.forEach((p: any) => {
              next.set(p.user_id, {
                is_online: !!p.is_online,
                last_seen: p.last_seen ?? null,
                updated_at: p.updated_at ?? null
              });
            });
            return next;
          });
        }
      } catch (error) {
        console.warn('🚫 UserPresence: Error setting up presence, continuing with degraded mode:', error);
        // Don't set initError here - just continue with degraded mode
        // The user can still use the app, just without real-time presence updates
        if (user?.id) {
          const currentTime = new Date().toISOString();
          updatePresenceRecord(user.id, true, currentTime);
        }
      }
    };

    setupPresence();

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('👤 UserPresence: Tab became visible, updating presence');
        updatePresence(true);
      }
    };

    const handleBeforeUnload = () => {
      // Avoid DB writes during unload (auth may be unavailable); update local state only
      const currentTime = new Date().toISOString();
      if (user?.id) updatePresenceRecord(user.id, false, currentTime);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      console.log('🔴 UserPresence: Cleaning up for user:', user?.id);

      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }

      if (presenceChannelRef.current) {
        try { supabase.removeChannel(presenceChannelRef.current); } catch (e) { console.warn('⚠️ UserPresence: Error removing presence channel', e); }
        presenceChannelRef.current = null;
        presenceSubscribedRef.current = false;
      }

      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const value: UserPresenceContextType = {
    onlineUsers: derivedOnlineUsers,
    isOnline
  };

  return (
    <UserPresenceContext.Provider value={value}>
      {children}
    </UserPresenceContext.Provider>
  );
};

// Exported wrapper that catches all errors gracefully
export const UserPresenceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <PresenceErrorBoundary>
      <UserPresenceProviderInner>
        {children}
      </UserPresenceProviderInner>
    </PresenceErrorBoundary>
  );
};

export const useUserPresence = () => {
  const context = useContext(UserPresenceContext);
  if (!context) {
    console.warn('useUserPresence used outside of provider – returning safe defaults');
    return defaultContextValue;
  }
  return context;
};
